import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { RotateCcw } from 'lucide-react';
type Vertical = 'odonto' | 'inmobiliaria' | 'servicios' | 'educacion' | 'ecommerce' | 'estetica';
interface VerticalContent {
  label: string;
  clientMsg: string;
  aiReply: string;
  panel1Title: string;
  panel1: {
    k: string;
    v: string;
  }[];
  panel2Title: string;
  panel2Chips: string[];
  finalText: string;
  followupText: string;
}
const content: Record<Vertical, VerticalContent> = {
  odonto: {
    label: 'Odontología',
    clientMsg: 'Hola, quiero información sobre diseño de sonrisa.',
    aiReply: 'Claro, puedo ayudarte con información, disponibilidad y agendamiento. ¿Quieres conocer el procedimiento o prefieres consultar horarios?',
    panel1Title: 'LEAD CALIFICADO',
    panel1: [{
      k: 'Servicio',
      v: 'Diseño de sonrisa'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'AGENDA',
    panel2Chips: ['Mañana 10:00 AM', '3:00 PM'],
    finalText: 'CITA AGENDADA',
    followupText: 'SEGUIMIENTO AUTOMÁTICO'
  },
  inmobiliaria: {
    label: 'Inmobiliaria',
    clientMsg: 'Hola, vi el apartamento de 3 habitaciones. Quiero visitarlo.',
    aiReply: 'Claro, puedo darte el precio, las características o agendar una visita.',
    panel1Title: 'LEAD CAPTURADO',
    panel1: [{
      k: 'Interés',
      v: 'Apto. 3 habitaciones'
    }, {
      k: 'Intención',
      v: 'Alta'
    }, {
      k: 'Estado',
      v: 'Calificado'
    }],
    panel2Title: 'CRM Y AGENDA',
    panel2Chips: ['CRM actualizado', 'Disponibilidad consultada'],
    finalText: 'VISITA AGENDADA',
    followupText: 'SEGUIMIENTO AUTOMÁTICO'
  },
  servicios: {
    label: 'Servicios',
    clientMsg: 'Necesito una cotización para mi empresa.',
    aiReply: 'Claro, ¿qué servicio necesitas y para cuántas personas aproximadamente?',
    panel1Title: 'SOLICITUD CALIFICADA',
    panel1: [{
      k: 'Servicio',
      v: 'Consultoría'
    }, {
      k: 'Tamaño',
      v: '20-50 personas'
    }, {
      k: 'Intención',
      v: 'Alta'
    }],
    panel2Title: 'CRM',
    panel2Chips: ['Cotización generada', 'Asignado a ventas'],
    finalText: 'COTIZACIÓN ENVIADA',
    followupText: 'SEGUIMIENTO AUTOMÁTICO'
  },
  educacion: {
    label: 'Educación',
    clientMsg: 'Quiero información sobre el programa.',
    aiReply: 'Claro, puedo explicarte el programa, requisitos, duración y agendar una llamada con un asesor.',
    panel1Title: 'LEAD CALIFICADO',
    panel1: [{
      k: 'Programa',
      v: 'Maestría en Marketing'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'ASESOR',
    panel2Chips: ['Asignado', 'Llamada agendada'],
    finalText: 'LLAMADA AGENDADA',
    followupText: 'SEGUIMIENTO AUTOMÁTICO'
  },
  ecommerce: {
    label: 'E-commerce',
    clientMsg: '¿Tienen disponible este producto?',
    aiReply: 'Sí, puedo ayudarte con disponibilidad, características y el proceso de compra.',
    panel1Title: 'CLIENTE',
    panel1: [{
      k: 'Producto',
      v: 'Disponible'
    }, {
      k: 'Interés',
      v: 'Compra directa'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'COMPRA',
    panel2Chips: ['Pedido confirmado', 'Postventa activa'],
    finalText: 'COMPRA CONFIRMADA',
    followupText: 'SEGUIMIENTO POSTVENTA'
  },
  estetica: {
    label: 'Estética',
    clientMsg: 'Quiero información sobre depilación láser.',
    aiReply: 'Claro, puedo darte información sobre el servicio y consultar disponibilidad.',
    panel1Title: 'LEAD CALIFICADO',
    panel1: [{
      k: 'Servicio',
      v: 'Depilación láser'
    }, {
      k: 'Interés',
      v: 'Alto'
    }, {
      k: 'Fuente',
      v: 'WhatsApp'
    }],
    panel2Title: 'AGENDA',
    panel2Chips: ['Mañana 11:00 AM', '4:00 PM'],
    finalText: 'CITA AGENDADA',
    followupText: 'SEGUIMIENTO AUTOMÁTICO'
  }
};
const verticalOrder: Vertical[] = ['odonto', 'inmobiliaria', 'servicios', 'educacion', 'ecommerce', 'estetica'];
const STEP_DELAYS = [200, 1300, 2600, 3800, 5000, 6200];
export function DemoSection() {
  const [vertical, setVertical] = useState<Vertical>('odonto');
  const [step, setStep] = useState(0);
  const [playKey, setPlayKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, {
    once: true,
    amount: 0.4
  });
  const data = content[vertical];
  useEffect(() => {
    if (!isInView) return;
    const prefersReduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setStep(6);
      return;
    }
    const timers = STEP_DELAYS.map((delay, idx) => setTimeout(() => setStep(idx + 1), delay));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isInView, playKey]);
  const replay = () => {
    setStep(0);
    setPlayKey(k => k + 1);
  };
  const selectVertical = (key: Vertical) => {
    if (key === vertical) return;
    setVertical(key);
    replay();
  };
  return <section id="como-funciona" className="bg-arena py-[72px] md:py-[120px]" data-id="element-53" data-mp="1bmnqdqslkqlz">
      <div className="max-w-[1000px] mx-auto px-6 md:px-20" data-id="element-54" data-mp="1gkp5k0mwk5s6">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="text-center max-w-[560px] mx-auto mb-10" data-id="element-55" data-mp="1llcy4qqs4uoi">
          <span className="block font-body text-[11px] uppercase font-medium text-salvia tracking-[0.15em] mb-4" data-id="element-56" data-mp="12tke3ltoujsx">
            ORIGEN EN ACCIÓN
          </span>
          <h2 className="font-display text-[32px] md:text-[44px] font-bold text-carbon leading-[1.1]" data-id="element-57" data-mp="a5xzliix9ura">
            No te lo contamos. Te lo mostramos.
          </h2>
        </motion.div>

        {/* Vertical toggle */}
        <div className="flex items-center justify-center gap-2 mb-3 flex-wrap" data-id="element-58" data-mp="73tgtbpldslf">
          {verticalOrder.map(key => <button key={key} onClick={() => selectVertical(key)} className={`font-body text-[13px] font-medium px-4 py-2.5 rounded-[6px] border transition-all duration-200 ${vertical === key ? 'bg-verde text-arena border-verde' : 'bg-transparent text-carbon/60 border-carbon/15 hover:text-carbon hover:border-carbon/30'}`} data-id="element-59" data-mp="24gxh0xxj3nl">
              {content[key].label}
            </button>)}
        </div>
        <p className="text-center font-body text-[12px] text-carbon/40 italic mb-12 max-w-[480px] mx-auto" data-id="element-60" data-mp="3u6yafwvr6w4p">
          Ejemplos de aplicación — ORIGEN se adapta a distintos negocios con
          procesos de atención, calificación y agendamiento.
        </p>

        {/* Demo card */}
        <div ref={containerRef} className="bg-carbon rounded-[12px] p-6 md:p-10 max-w-[520px] mx-auto" data-id="element-61" data-mp="14j7c4xtpp900">
          <div className="space-y-3 min-h-[320px]" data-id="element-62" data-mp="1h2ber19gu9da">
            {/* Client message */}
            <motion.div animate={step >= 1 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-end" data-id="element-63" data-mp="1gzynfxhtimnk">
              <div className="bg-arena text-carbon font-body text-[14px] rounded-[12px] rounded-tr-none px-4 py-2.5 max-w-[85%]" data-id="element-64" data-mp="1uu9gus4rf4zy">
                {data.clientMsg}
              </div>
            </motion.div>

            {/* AI reply */}
            <motion.div animate={step >= 2 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="flex justify-start" data-id="element-65" data-mp="1q2zsduuy67pn">
              <div className="bg-verde text-arena font-body text-[14px] rounded-[12px] rounded-tl-none px-4 py-2.5 max-w-[85%]" data-id="element-66" data-mp="2ilevx52vlt97">
                {data.aiReply}
              </div>
            </motion.div>

            {/* Panel 1 */}
            <motion.div animate={step >= 3 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3 mt-4" data-id="element-67" data-mp="1k2lacf7c8r5k">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-azul mb-1.5" data-id="element-68" data-mp="27s5tay3hroeu">
                {data.panel1Title}
              </span>
              <div className="font-body text-[13px] text-arena/80 space-y-0.5" data-id="element-69" data-mp="1xu9o0mx8jnon">
                {data.panel1.map(row => <p key={row.k} data-id="element-70" data-mp="rep8yx6tqb2g">
                    {row.k}: {row.v}
                  </p>)}
              </div>
            </motion.div>

            {/* Panel 2 */}
            <motion.div animate={step >= 4 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="bg-arena/5 border border-arena/15 rounded-[12px] px-4 py-3" data-id="element-71" data-mp="2y2ptl8hq3zvs">
              <span className="block font-body text-[10px] uppercase tracking-[0.1em] text-salvia mb-2" data-id="element-72" data-mp="3crm0h281n6ut">
                {data.panel2Title}
              </span>
              <div className="flex flex-wrap gap-2" data-id="element-73" data-mp="3hsrt701peu4c">
                {data.panel2Chips.map(chip => <span key={chip} className="font-body text-[12px] text-arena/80 bg-arena/10 rounded-full px-3 py-1" data-id="element-74" data-mp="3qr3txuwab8ai">
                    {chip}
                  </span>)}
              </div>
            </motion.div>

            {/* Final */}
            <motion.div animate={step >= 5 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="font-body text-[14px] font-medium text-arena flex items-center gap-2 pt-1" data-id="element-75" data-mp="21bxg8im6clxp">
              {data.finalText} <span className="text-verde" data-id="element-76" data-mp="2g5qd8bu8nlez">✓</span>
            </motion.div>

            {/* Follow-up */}
            <motion.div animate={step >= 6 ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 8
          }} transition={{
            duration: 0.3
          }} className="font-body text-[14px] font-medium text-arena/70 flex items-center gap-2" data-id="element-77" data-mp="2ka7ti11e3gl0">
              {data.followupText} <span className="text-verde" data-id="element-78" data-mp="33xdstxm1va1y">✓</span>
            </motion.div>
          </div>

          <button onClick={replay} className="mt-6 inline-flex items-center gap-1.5 font-body text-[12px] text-arena/50 hover:text-arena/80 transition-opacity duration-150" data-id="element-79" data-mp="37yqxi4dep77a">
            <RotateCcw size={12} data-id="element-80" data-mp="11agxezf38tmi" /> Ver de nuevo
          </button>
        </div>
      </div>
    </section>;
}